import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Spin, Row, Col, Typography, theme, Drawer, Button } from "antd";
import { LoadingOutlined, CloseOutlined } from "@ant-design/icons";
import TicketServices, {
  ITicket,
  TicketStatus,
  TicketDependencyType,
} from "../../../Services/TicketServices";
import UserService from "../../../Services/UserService";
import { ApplicationUserStatus } from "../../../Common/CommonComponent";
import { adminContext } from "../../../Common/PageRoute";

// Modular sub-components
import { TicketHeader } from "./TicketHeader";
import { TicketSteps } from "./TicketSteps";
import { TicketProperties } from "./TicketProperties";
import { TicketThread } from "./TicketThread";
import { TicketReply } from "./TicketReply";
import { downloadFile } from "./utils";
//@ts-ignore: CSS import issue
import "./TicketDetails.css";

const { Text } = Typography;

const TicketDetailsContainer = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { openNotification } = useContext(adminContext);
  const { token } = theme.useToken();

  const premiumShadow = token.boxShadowTertiary;
  const subtleBorder = `1px solid ${token.colorBorderSecondary}`;

  // General loading states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [refreshingThread, setRefreshingThread] = useState(false);

  // Ticket data and threads
  const [ticket, setTicket] = useState<ITicket | null>(null);
  const [assigneeOptions, setAssigneeOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  // Composer values
  const [reply, setReply] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isPropertiesDrawerOpen, setIsPropertiesDrawerOpen] = useState(false);
  const [shiftModalTrigger, setShiftModalTrigger] = useState(0);

  const threadEndRef = useRef<HTMLDivElement>(null);

  // Fetch ticket details
  const fetchTicket = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await TicketServices.get(id!);
      if (res) {
        const ticketDetails = res?.ticketDetails || res?.TicketDetails || res;
        const threads =
          res?.threads ||
          res?.Threads ||
          ticketDetails?.threads ||
          ticketDetails?.Threads ||
          [];
        if (ticketDetails) {
          setTicket({ ...ticketDetails, threads });
          const ticketNum =
            ticketDetails?.number ||
            ticketDetails?.Number ||
            ticketDetails?.N ||
            id;
          if (typeof window !== "undefined") {
            (window as any).dynamicPathLabels =
              (window as any).dynamicPathLabels || {};
            (window as any).dynamicPathLabels[id!] = `Ticket #${ticketNum}`;
            window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
          }
        } else {
          openNotification("error", "Ticket details not found.");
          navigate("/tickets");
        }
      } else {
        openNotification("error", "Ticket not found.");
        navigate("/tickets");
      }
    } catch (error) {
      openNotification(
        "error",
        "An unexpected error occurred while loading the ticket.",
      );
      navigate("/tickets");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch staff assignee users
  const fetchAssigneeOptions = async () => {
    setAssigneeLoading(true);
    try {
      const res = await UserService.getUserOptions(true, true);

      if (res?.status) {
        const options = (res?.result?.items || [])
          .filter((item: any) => item?.id)
          .map((item: any) => ({
            label: item?.name || "",
            value: String(item?.id),
          }))
          .sort((a: any, b: any) =>
            (a.label || "").localeCompare(b.label || "", undefined, {
              sensitivity: "base",
              numeric: true,
            })
          );
        setAssigneeOptions(options);
      } else {
        openNotification(
          "error",
          res?.message || "Failed to fetch assignee users.",
        );
      }
    } catch (error) {
      openNotification(
        "error",
        "An unexpected error occurred while loading assignee users.",
      );
    } finally {
      setAssigneeLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (id) {
      fetchTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchAssigneeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ticket?.threads) {
      scrollToBottom();
    }
  }, [ticket?.threads]);

  const scrollToBottom = () => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Action: Post reply / internal note
  const handleSendReply = async (
    isLinked: boolean = false,
    attachmentFiles?: any[],
  ): Promise<boolean> => {
    if (!reply.trim() && (!attachmentFiles || attachmentFiles.length === 0))
      return false;

    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
    if (attachmentFiles && attachmentFiles.length > 0) {
      for (const item of attachmentFiles) {
        const rawFile = item.file || item;
        if (rawFile instanceof File && rawFile.size > MAX_FILE_SIZE) {
          openNotification(
            "error",
            `Attachment "${rawFile.name}" exceeds the maximum allowed size of 25 MB.`,
          );
          return false;
        }
      }
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("message", reply);
      formData.append("isPrivate", String(isPrivate));
      formData.append("updateLinked", String(isLinked));

      if (attachmentFiles && attachmentFiles.length > 0) {
        attachmentFiles.forEach((item: any) => {
          const rawFile = item.file || item;
          if (rawFile instanceof File) {
            formData.append("files", rawFile, rawFile.name);
          }
        });
      }

      const res = await TicketServices.postReply(id!, formData);
      if (res?.status) {
        setReply("");
        setIsPrivate(false);
        await fetchTicket(true); // silent refresh
        return true;
      } else {
        openNotification("error", res?.message || "Failed to send reply.");
      }
    } catch (error) {
      openNotification(
        "error",
        "An unexpected error occurred while posting reply.",
      );
    } finally {
      setSubmitting(false);
    }
    return false;
  };

  // Action: PDF Report Download
  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      const res = await TicketServices.downloadTicketPdf(id!);
      if (res && res.status === "done") {
        openNotification("success", "Ticket PDF report downloaded.");
      } else {
        openNotification("error", "Failed to download PDF.");
      }
    } catch (error) {
      openNotification("error", "Error downloading PDF.");
    } finally {
      setLoading(false);
    }
  };

  // Action: Attachment download via SecureMedia token endpoint
  const handleDownloadAttachment = async (file: any) => {
    const ticketId =
      id || ticket?.id || (ticket as any)?.Id || (ticket as any)?._id;
    const fileId =
      file?.id ||
      file?.Id ||
      (typeof file === "string" ? file : file?.name || file?.Name);
    const fileName =
      file?.name ||
      file?.Name ||
      (typeof file === "string" ? file : "attachment");

    if (!ticketId || !fileId) return;

    try {
      setDownloading(true);
      const res: any = await TicketServices.getAttachmentUrl(ticketId, fileId);

      const downloadUrl =
        res?.result?.url ||
        res?.result?.Url ||
        (typeof res?.result === "string" ? res.result : null) ||
        (typeof res === "string" ? res : null);

      if (!res?.status || !downloadUrl) {
        openNotification(
          "error",
          res?.message || "Failed to download attachment.",
        );
        return;
      }

      await downloadFile(downloadUrl, fileName);
      openNotification("success", `${fileName} downloaded successfully.`);
    } catch {
      openNotification("error", "Error downloading file.");
    } finally {
      setDownloading(false);
    }
  };

  // Action: Assign support agent confirmed (called via inline popconfirm)
  const handleAssignChange = async (userId: string) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.updateAgent(id!, userId);
      if (res?.status) {
        openNotification("success", "Ticket assigned successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Assignment failed.");
      }
    } catch (error) {
      openNotification("error", "Error assigning ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolutionETAChange = async (date: any, reason?: string) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.setResolutionETA(id!, date, reason);
      if (res?.status) {
        openNotification(
          "success",
          date
            ? "Resolution ETA updated successfully."
            : "Resolution ETA cleared.",
        );
        await fetchTicket(true);
      } else {
        openNotification(
          "error",
          res?.message || "Failed to update resolution ETA.",
        );
      }
    } catch (error) {
      openNotification("error", "Error updating resolution ETA.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDueDateChange = async (date: any, reason?: string) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.setDueDate(id!, date, reason);
      if (res?.status) {
        openNotification(
          "success",
          date ? "Due date updated successfully." : "Due date cleared.",
        );
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to update due date.");
      }
    } catch (error) {
      openNotification("error", "Error updating due date.");
    } finally {
      setSubmitting(false);
    }
  };

  // Action: Client input required submitted (via inline popover)
  const handleInputRequiredToggle = async (
    checked: boolean,
    reason: string,
  ) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.setInputRequired(id!, checked, reason);
      if (res?.status) {
        openNotification("success", `Input request sent successfully.`);
        await fetchTicket(true);
      } else {
        openNotification(
          "error",
          res?.message || "Failed to toggle input required flag.",
        );
      }
    } catch (error) {
      openNotification(
        "error",
        "An unexpected error occurred during input required change.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Action: Close confirmed
  const handleCloseConfirm = async (
    reason: string,
    updateLinked: boolean = false,
  ) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.close(id!, reason, updateLinked);
      if (res?.status) {
        openNotification("success", "Ticket closed successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to close ticket.");
      }
    } catch (error) {
      openNotification("error", "Error closing ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  // Action: Reopen confirmed
  const handleReopenConfirm = async (reason: string) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.reopen(id!, reason);
      if (res?.status) {
        openNotification("success", "Ticket reopened successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to reopen ticket.");
      }
    } catch (error) {
      openNotification("error", "Error reopening ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  // Action: Status transition confirmed
  const handleStatusTransitionConfirm = async (
    nextStatus: number,
    reason: string,
    updateLinked?: boolean,
  ) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.changeStatus(id!, {
        newStatus: nextStatus,
        reason,
        updateLinked,
      });
      if (res?.status) {
        openNotification("success", "Ticket status updated successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to update status.");
      }
    } catch (error) {
      openNotification("error", "Error updating status.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePriorityChange = async (priority: number) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.updatePriority(id!, priority);
      if (res?.status) {
        openNotification("success", "Ticket priority updated successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to update priority.");
      }
    } catch (error) {
      openNotification("error", "Error updating priority.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleModuleChange = async (module: number) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.updateModule(id!, module);
      if (res?.status) {
        openNotification("success", "Ticket module updated successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to update module.");
      }
    } catch (error) {
      openNotification("error", "Error updating module.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssueTypeChange = async (issueType: number, reason: string) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.updateIssueType(id!, issueType, reason);
      if (res?.status) {
        openNotification("success", "Ticket type updated successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to update ticket type.");
      }
    } catch (error) {
      openNotification("error", "Error updating ticket type.");
    } finally {
      setSubmitting(false);
    }
  };



  const handleUnmergeConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await TicketServices.unmergeTickets(id!);
      if (res?.status) {
        openNotification("success", "Ticket unmerged successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to unmerge ticket.");
      }
    } catch (error) {
      openNotification("error", "Error unmerging ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlinkConfirm = async (removeAllInterconnected: boolean) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.unlinkTickets(
        id!,
        removeAllInterconnected,
      );
      if (res?.status) {
        openNotification("success", res?.message || "Tickets unlinked successfully.");
        await fetchTicket(true);
      } else {
        openNotification("error", res?.message || "Failed to unlink tickets.");
      }
    } catch (error) {
      openNotification("error", "Error unlinking tickets.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCollaboratorsChange = async (userIds: string[]) => {
    setSubmitting(true);
    try {
      const res = await TicketServices.updateCollaborators([id!], userIds);
      if (res?.status) {
        openNotification("success", "Collaborators updated successfully.");
        await fetchTicket(true);
      } else {
        openNotification(
          "error",
          res?.message || "Failed to update collaborators.",
        );
      }
    } catch (error) {
      openNotification("error", "Error updating collaborators.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="ticket-details-container"
      style={{ maxWidth: 1400, margin: "0 auto" }}
    >
      {/* Dynamic Slim Page Header */}
      <TicketHeader
        ticket={ticket}
        id={id}
        loading={loading}
        onDownloadPdf={handleDownloadPdf}
        onBack={() => navigate("/tickets")}
        onOpenProperties={() => setIsPropertiesDrawerOpen(true)}
        onShiftToCapsiTask={() => setShiftModalTrigger((prev) => prev + 1)}
        onRefresh={async () => {
          setRefreshingThread(true);
          await fetchTicket(true);
          setRefreshingThread(false);
        }}
        refreshing={refreshingThread}
      />

      {loading && !ticket ? (
        <Card
          style={{
            textAlign: "center",
            borderRadius: token.borderRadiusLG,
            boxShadow: premiumShadow,
            border: subtleBorder,
            padding: 40,
          }}
        >
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />} />
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 12, fontSize: 13 }}
          >
            Fetching ticket information...
          </Text>
        </Card>
      ) : ticket ? (
        <Row gutter={[24, 24]} className="ticket-details-row">
          {/* Main workspace column: Left Panel */}
          <Col xs={24} xl={17} xxl={18} className="ticket-details-left-col">
            <div
              className="ticket-details-left-panel"
              style={{
                background: token.colorBgContainer,
                borderRadius: token.borderRadiusLG,
                border: subtleBorder,
                boxShadow: premiumShadow,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Chronological stage Steps Indicator */}
              <TicketSteps ticket={ticket} />

              {/* Core thread timelines, message description and attachments list */}
              <TicketThread
                ticket={ticket}
                onDownloadAttachment={handleDownloadAttachment}
                downloading={downloading}
                threadEndRef={threadEndRef as any}
                onRefreshThread={async () => {
                  setRefreshingThread(true);
                  await fetchTicket(true);
                  setRefreshingThread(false);
                }}
                refreshingThread={refreshingThread}
              />

              {/* Composer Reply field */}
              {!ticket.isEscalated &&
                ![TicketStatus.Closed].includes(ticket.status) && (
                  <TicketReply
                    reply={reply}
                    isLinked={
                      ticket?.dependencies?.some(
                        (d) => d.type === TicketDependencyType.Linked,
                      ) ?? false
                    }
                    onChangeReply={setReply}
                    isPrivate={isPrivate}
                    onChangePrivate={setIsPrivate}
                    submitting={submitting}
                    onSendReply={handleSendReply}
                  />
                )}
            </div>
          </Col>

          {/* Properties sidebar column: Right Panel */}
          <Col xs={24} xl={7} xxl={6} className="ticket-details-right-col">
            <TicketProperties
              ticket={ticket}
              submitting={submitting}
              assigneeLoading={assigneeLoading}
              assigneeOptions={assigneeOptions}
              onAssignChange={handleAssignChange}
              onStatusTransitionConfirm={handleStatusTransitionConfirm}
              onResolutionETAChange={handleResolutionETAChange}
              onDueDateChange={handleDueDateChange}
              onInputRequiredToggle={handleInputRequiredToggle}
              onCloseConfirm={handleCloseConfirm}
              onReopenConfirm={handleReopenConfirm}
              onPriorityChange={handlePriorityChange}
              onModuleChange={handleModuleChange}
              onIssueTypeChange={handleIssueTypeChange}
              onUnmergeConfirm={handleUnmergeConfirm}
              onUnlinkConfirm={handleUnlinkConfirm}
              onCollaboratorsChange={handleCollaboratorsChange}
              onRefresh={() => fetchTicket(true)}
              shiftModalTrigger={shiftModalTrigger}
            />
          </Col>
        </Row>
      ) : (
        <Card
          style={{
            textAlign: "center",
            borderRadius: token.borderRadiusLG,
            boxShadow: premiumShadow,
            border: subtleBorder,
            padding: 30,
          }}
        >
          <Text type="secondary">No ticket details are available.</Text>
        </Card>
      )}
      {ticket && (
        <Drawer
          title="Ticket Actions & Properties"
          placement="right"
          width={350}
          onClose={() => setIsPropertiesDrawerOpen(false)}
          open={isPropertiesDrawerOpen}
          styles={{ body: { padding: 0 } }}
          destroyOnClose
          closable={false}
          extra={
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: 16 }} />}
              onClick={() => setIsPropertiesDrawerOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
              }}
            />
          }
        >
          <TicketProperties
            ticket={ticket}
            submitting={submitting}
            assigneeLoading={assigneeLoading}
            assigneeOptions={assigneeOptions}
            onAssignChange={handleAssignChange}
            onStatusTransitionConfirm={handleStatusTransitionConfirm}
            onResolutionETAChange={handleResolutionETAChange}
            onDueDateChange={handleDueDateChange}
            onInputRequiredToggle={handleInputRequiredToggle}
            onCloseConfirm={handleCloseConfirm}
            onReopenConfirm={handleReopenConfirm}
            onPriorityChange={handlePriorityChange}
            onModuleChange={handleModuleChange}
            onUnmergeConfirm={handleUnmergeConfirm}
            onUnlinkConfirm={handleUnlinkConfirm}
            onCollaboratorsChange={handleCollaboratorsChange}
            onRefresh={() => fetchTicket(true)}
          />
        </Drawer>
      )}
    </div>
  );
};

export default TicketDetailsContainer;
